import i18next from 'i18next';
import { Search } from '../@types/Search';
import { PRODUCT_CARD_IMAGE_SPECS, toProductCard } from '../Components/ProductCard/utils';
import { DEFAULT_PLACEHOLDER_IMAGE } from '../utils/constants';
import { imageSrcSet, imageUrl } from '../utils/image';
import { extractDataFromElement } from '../utils/data';
import { toSearchIndex } from '../Gql/adapter';
import { gqlQuery } from '../Gql/client';
import { PublicApiProps, resolvePublicApi } from '../Gql/config';
import { migrateLegacyParams } from '../Gql/legacyParams';
import { SITE_SEARCH_QUERY } from '../Gql/queries';
import { toSearchVariables } from '../Gql/searchParams';
import { SiteSearchData } from '../Gql/types';
import ready from '../utils/ready';
import sanitize from '../utils/sanitize';

type ProductCardListProps = PublicApiProps & {
  url: string;
  href?: string;
  searchParams?: string;
};

const DEFAULT_HITS = 3;

const getFullUrl = (url: string, query?: string) => `${url}${query ? `${query}` : ''}`;

const getVehicles = (url: string, apiKey?: string, query?: string) =>
  fetch(getFullUrl(url, query), {
    headers: apiKey
      ? {
          'x-api-key': apiKey,
        }
      : {},
  });

const localCache: { [key: string]: Search | undefined } = {};
const setCache = (key: string, value: Search) => {
  localCache[key] = value;
};
const getCache = (key: string) => localCache[key];

class ProductCardList {
  private props: ProductCardListProps;
  private element: HTMLUListElement;
  private vehicles?: Search;
  private displayBranchName: boolean = false;

  constructor(_element: HTMLUListElement) {
    this.element = _element;
    const _props = extractDataFromElement<ProductCardListProps>(_element, 'data-props');
    if (!_props) throw 'Missing props';
    this.props = _props;

    this.fetchData();

    this.render();
  }

  /** The grid's vehicles from `search.ads`, scoped to the site. */
  fetchPublicApi = async () => {
    const publicApi = resolvePublicApi(this.props);
    if (!publicApi) return false;
    const raw = new URLSearchParams(this.props.searchParams || '');
    const perPage = Number.parseInt(raw.get('hits') || '', 10) || DEFAULT_HITS;
    const params = migrateLegacyParams(raw);
    try {
      const data = await gqlQuery<SiteSearchData>(publicApi.api, SITE_SEARCH_QUERY, {
        ...toSearchVariables(params, publicApi.market, { page: 1, perPage }),
        branchGroupId: publicApi.branchGroupId,
        withFacets: false,
      });
      const ads = data.search?.ads;
      if (ads) {
        const index = toSearchIndex(ads, params);
        const branchIds = new Set(
          index.documentList.documents.flatMap((d) => d.branches.map((b) => b.id))
        );
        this.displayBranchName = branchIds.size > 1;
        this.vehicles = { ...index, facets: [] } as unknown as Search;
        this.render();
      }
    } catch (e) {
      //eslint-disable-next-line
      console.log('error', e);
    }
    return true;
  };

  fetchData = async () => {
    if (await this.fetchPublicApi()) return;
    const cacheKey = getFullUrl(this.props.url, this.props.searchParams);
    this.vehicles = getCache(cacheKey);
    if (this.vehicles) {
      const branches = this.vehicles.facets.find((x) => x.id === 'branch')?.filters.length;
      this.displayBranchName = branches !== undefined && branches > 1;
      return;
    }

    try {
      const response = await getVehicles(
        this.props.url,
        this.props.apiKey,
        this.props.searchParams
      );
      if (response.ok) {
        const asJson = (await response.json()) as Search;
        this.vehicles = asJson;
        setCache(cacheKey, asJson);
        this.render();
      }
    } catch (e) {
      //eslint-disable-next-line
      console.log('error', e);
    }
  };

  render = () => {
    this.element.innerHTML =
      sanitize(
        this.vehicles?.documentList.documents
          .map((_vehicle) => {
            const vehicle = toProductCard(_vehicle, this.displayBranchName);
            return `
                <li class="overflow-grid__item">
                    <article class="product-card">
                        <div class="product-card__image-wrapper">
                            ${
                              vehicle.image
                                ? `
                                <picture>
                                    <source
                                        type="image/webp"
                                        data-srcset="${imageSrcSet(vehicle.image, PRODUCT_CARD_IMAGE_SPECS, true)}"
                                        data-sizes="(min-width: 600px) calc(((100vw - 48px) / 2) - 8px), (min-width: 900px) calc(((100vw - 48px) / 3) - 10.666px), (min-width: 1312px) 410px, calc(100vw - 32px)"
                                    />
                                    <img
                                        data-srcset="${imageSrcSet(vehicle.image, PRODUCT_CARD_IMAGE_SPECS)}"
                                        data-sizes="(min-width: 600px) calc(((100vw - 48px) / 2) - 8px), (min-width: 900px) calc(((100vw - 48px) / 3) - 10.666px), (min-width: 1312px) 410px, calc(100vw - 32px)"
                                        data-src="${imageUrl(vehicle.image, { width: 411, height: 274 })}"
                                        alt="${vehicle.title} ${vehicle.description}"
                                        class="product-card__image lazyload"
                                    />
                                </picture>
                            `
                                : `
                                <picture>
                                    <img src="${DEFAULT_PLACEHOLDER_IMAGE}" class="product-card__image" alt="${vehicle.title} ${vehicle.description}" />
                                </picture>
                            `
                            }
                        </div>
                        <div class="product-card__content">
                            <div class="product-card__content-body">
                                ${
                                  vehicle.branchName
                                    ? `<div class="product-card__preheading">${vehicle.branchName}</div>`
                                    : ''
                                }
                                <div class="product-card__heading">
                                    ${
                                      this.props.href
                                        ? `
                                        <a
                                            href="${this.props.href}${vehicle.id}/${vehicle.slug}"
                                            title="${vehicle.title}"
                                            aria-label="${vehicle.title}"
                                            class="product-card__link"
                                        >
                                            ${vehicle.title}
                                        </a>`
                                        : vehicle.title
                                    }
                                </div>
                                ${
                                  vehicle.description
                                    ? `<div class="product-card__description">${vehicle.description}</div>`
                                    : ''
                                }
                                ${
                                  vehicle.uspList
                                    ? `
                                    <div class="product-card__usp">
                                        <ul class="usp-list usp-list--small">
                                            ${vehicle.uspList
                                              .map(
                                                (usp) => `
                                                <li class="usp-list__item">
                                                    <div class="usp-list__box">
                                                        ${usp.title}
                                                    </div>
                                                </li>
                                            `
                                              )
                                              .join('')}
                                        </ul>
                                    </div>`
                                    : ''
                                }
                            </div>
                            <div class="product-card__content-footer">
                                <div class="product-card__price">
                                    <div class="product-card__price-cell">
                                        ${
                                          vehicle.oldPrice
                                            ? `<div class="product-card__price-old">${vehicle.oldPrice}</div>`
                                            : `<div class="product-card__price-current">${i18next.t('other.price')}</div>`
                                        }
                                        <div class="product-card__price-current">${
                                          vehicle.price
                                        }</div>
                                    </div>
                                    ${
                                      vehicle.leasingPrice
                                        ? `
                                        <div class="product-card__price-cell">
                                            <div class="product-card__price-label">${i18next.t('other.privateLeasingPrice')}</div>
                                            <div class="product-card__price-current">${vehicle.leasingPrice}</div>
                                        </div>
                                    `
                                        : ''
                                    }
                                </div>
                            </div>
                        </div>
                    </article>
                </li>
            `;
          })
          .join('')
      ) || '';
  };
}

const initialize = () => {
  document
    .querySelectorAll<HTMLDivElement>('[data-module-type="product-grid"]')
    .forEach((element) => {
      const listElement = element.querySelector<HTMLUListElement>('.overflow-grid__list');
      if (listElement) {
        new ProductCardList(listElement);
      }
    });
};

ready(initialize);
